// Shared helpers for the v2 test result views (TestResultV2, RetakeResultSummary)
// and the TestBoxV2 card, so status colors, name formatting, "latest attempt"
// selection, and score math stay consistent across them.
import getStudentName from '../../../../utils/getStudentName';

export const sessionStatusColor = (status) =>
    ({ in_progress: 'warning', submitted: 'info', graded: 'success' }[status] || 'default');

// Reuse the app-wide name formatter (null-safe — returns "Unknown" when empty),
// adding the consultation number used across the test result views.
export const formatStudentName = (student) => {
    if (!student) return '';
    const base = getStudentName(student);
    return student.consultation_number ? `${base} (${student.consultation_number})` : base;
};

// A session attempt is "done" once submitted or graded.
export const isSessionDone = (s) => s?.status === 'submitted' || s?.status === 'graded';

// Combined auto-graded + FRQ score for a session, so the summary is correct even
// if a retakeable test ever includes FRQ parts (not just MCQ/short-answer).
export const sessionScore = (s) => {
    const raw = (s.mcq_score || 0) + (s.frq_score || 0);
    const total = (s.mcq_total || 0) + (s.frq_total || 0);
    return { raw, total, pct: total ? raw / total : null };
};

// Deterministic "latest attempt" ordering: higher attempt_number wins, then later
// end_time, then higher id. Used wherever a single attempt must be chosen so the
// student card and the admin summary never disagree on a tie/missing value.
export const compareAttempts = (a, b) => {
    const an = a.attempt_number || 1, bn = b.attempt_number || 1;
    if (an !== bn) return an - bn;
    const at = a.end_time ? new Date(a.end_time).getTime() : 0;
    const bt = b.end_time ? new Date(b.end_time).getTime() : 0;
    if (at !== bt) return at - bt;
    return (a.id || 0) - (b.id || 0);
};

// Map of student_id -> their latest attempt (by compareAttempts).
export const latestSessionByStudent = (sessions = []) => {
    const map = new Map();
    for (const s of sessions) {
        const cur = map.get(s.student_id);
        if (!cur || compareAttempts(s, cur) > 0) map.set(s.student_id, s);
    }
    return map;
};

// Whether STUDENTS should see the correct answer in the results view. Vocab tests
// reveal it once grades are released (admins always see it via isAdmin); other
// tests keep the answer key hidden from students. `normalize_short_answers` is the
// vocab signal. Admin-ness is handled separately at the QuestionSlot level.
export const shouldRevealAnswersToStudent = (test, gradesReleased) =>
    !!gradesReleased && !!test?.normalize_short_answers;

// A test is a "vocab" test if any of its vocab-only flags are set. These are only
// ever set for the Vocab test type in the admin editor, so they don't false-trigger
// on regular tests. OR-ed for robustness regardless of which flag the API returns.
export const isVocabTest = (test) =>
    !!(test?.normalize_short_answers || test?.allow_retake || test?.questions_per_attempt);

// Past the due date, students lose access to a VOCAB test entirely — the card shows
// an "expired" message instead of the retake/result buttons. Admins always keep
// access (isAdmin short-circuit) and non-vocab tests are unaffected. Frontend-only
// guard: it hides the buttons but does not block the API/URL directly.
export const isExpiredForStudent = (courseTest, test, isAdmin) => {
    if (isAdmin) return false;
    if (!isVocabTest(test)) return false;
    const due = courseTest?.due_date;
    return !!due && new Date(due).getTime() < Date.now();
};
